import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import {Autocomplete, TextField} from '@mui/material';
import { useAddNewConsumableReqMutation } from '../../../../features/consumablerequest/consumableReqApiSlice';
import { useGetUsersQuery } from '../../../../features/users/usersApiSlice';
import useAuth from '../../../../features/hooks/useAuth';
import { useGetofficesQuery } from '../../../../features/offices/officesApiSlice';
import { useGetStoreOfficesQuery } from '../../../../features/storeoffices/storeofficesApiSlice';
import { useNavigate } from "react-router-dom";
import { HeadingWrap, Span, CommonButton } from '../../../../Styles/CommonStyles';
import nameStringFn from '../../../../data/nameStringFn';
import UnivConsumSelector from '../UnivConsumSelector';
import { useAlert } from '../../../ui/AlertProvider';

const Container = styled.div`
  margin-left: 10px;
  margin-right: 10px;
`
const Issuebutton = styled(CommonButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 50px;
  background-color: teal;
  margin-right: 30px;
  cursor: pointer;
  border-radius: 10px; 
`
const SingleSelectCont = styled.div`
  margin-top: 20px;
  margin-left: 20px;
  margin-bottom: 40px;
  width: 30%;
`
const RemarkContainer = styled.div`
  margin-top: 15px;
  margin-left:10px;
  margin-bottom: 20px;
  width: 90%;
`;

const SubHeadingField = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-left: 20px;
  margin-top: 20px;
  margin-bottom: 15px;
  font-family: "Roboto","Helvetica","Arial",sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #5C5891;  
`

const ConsumIssueReq = () => {
  // Using separate state hooks instead of consolidated formState
  const [remark, setRemark] = useState('');
  const [addSpecIndex, setAddSpecIndex] = useState(1);
  const [storeSelectedValue, setStoreSelectedValue] = useState('');
  const [selectedConsum, setSelectedConsum] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [storeOptions, setStoreOptions] = useState([]);
  
  const navigate = useNavigate();
  const alert = useAlert();

  //logged in user data
  const { id,  designation, designationSuffix, numSuffix } = useAuth();
  const user_current_office = useSelector((state) => state.office.off);


  //usercall/single user call
  const { getSingleuser } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data }) => ({
      getSingleuser: data?.entities
    }),
  });

  const { offices } = useGetofficesQuery('offices', {
    selectFromResult: ({ data }) => ({
      offices: data?.entities
    }),
  });

  const { storeoffices } = useGetStoreOfficesQuery('offices', {
    selectFromResult: ({ data }) => ({
      storeoffices: data?.entities
    }),
  });

  // Memoize user office and BO data to prevent unnecessary recalculations
  const userOfficeAndBO = useMemo(() => {
    const useroffice = offices && Object.values(offices || {})?.find(elm => elm.name === user_current_office);
    const Bo_id = useroffice?.Bo;
    
    const sendBoObj = Bo_id ? { 
      id: Bo_id, 
      name: getSingleuser && getSingleuser[Bo_id]?.name, 
      office: user_current_office, 
      designation: getSingleuser && getSingleuser[Bo_id]?.designation?.[user_current_office] || 'Unknown', 
      designationSuffix: getSingleuser && getSingleuser[Bo_id]?.designationSuffix?.[user_current_office] || '', 
      numSuffix: getSingleuser && getSingleuser[Bo_id]?.numSuffix?.[user_current_office] || '' 
    } : null;
    
    return { useroffice, Bo_id, sendBoObj };
  }, [offices, getSingleuser, user_current_office]);

  // Destructure for easier access
  const { Bo_id, sendBoObj } = userOfficeAndBO;

  // Add this useEffect to populate storeOptions - optimized with dependencies
  useEffect(() => {
    if (storeoffices) {
      const options = Object.values(storeoffices)
        .map(office => office.storeoffice);
      setStoreOptions(options);
    }
  }, [storeoffices]);
  
  const [addNewConsumableReq, { isLoading: isAddingReq }] = useAddNewConsumableReqMutation();


  // Memoize validation function
  const hasValidQuantity = useCallback((data) => {
    // Ensure that the input is a valid, non-empty array
    if (!Array.isArray(data) || data.length === 0) {
      return false;
    }
  
    // Check if all objects in the array have a quantity greater than 0
    return data.every(item => {
      if (!item || !item.quantity) return false;
      
      // Ensure that 'quantity' exists and is a valid number greater than 0
      const quantity = parseInt(item.quantity, 10);
      return !isNaN(quantity) && quantity > 0;
    });
  }, []);
  
  // Create a unique Id - memoized to avoid unnecessary recalculations
  const uniqueReqID = useCallback(() => {
    const dateString = Date.now().toString(36);
    const emailofuser = getSingleuser && getSingleuser[id]?.email 
      ? getSingleuser[id].email.slice(0, -8) 
      : 'user';
    return `cons-${emailofuser}-${dateString}`;
  }, [getSingleuser, id]);
  
  // Memoize this value to avoid recalculation on every render
  const uniqueReqId = useMemo(() => uniqueReqID(), [uniqueReqID]);
  
  // Memoize request data object
  const userReqData = useMemo(() => ({
    uniqueReqId: uniqueReqId,
    req_type: "ConsumableIssue",
    req_creator_id: id,
    req_creator_details: `${nameStringFn(getSingleuser?.[id] || {})}-${user_current_office}-${designation[user_current_office] || ''}`,
    req_store: storeSelectedValue,
    consumables_requested:[{ 
      time_stamp: new Date(), 
      actionNo: 1, 
      data: selectedConsum?.map(item => ({
        ...item,
        asset: item?.asset? item.asset.trim() : "Not Applicable",
        assettag: item?.assettag ? item.assettag : {label: "Not Applicable", value: "Not Applicable"},
      }))
    }],
    bo_partial_issue: false,
    bo_consum_issue:[],
    store_partial_issue: false,
    store_consum_issue:[],
    curr_status: 'Pending',
    created_at: new Date(),
    curr_sent_by: id,
    curr_sent_to: Bo_id,
    curr_sent_to_string: `${nameStringFn(getSingleuser?.[Bo_id] || {})}-${user_current_office}-${getSingleuser?.[Bo_id]?.designation?.[user_current_office] || 'Unknown'}`,
    curr_sent_to_tag: 'cosumureq_created',
    flow_users:[id],
    req_data: [{
      sent_to_tag:"cosumureq_created",
      Text: remark,
      sent_by: { 
        id: id, 
        name: getSingleuser && getSingleuser[id]?.name, 
        office: user_current_office, 
        designation: designation[user_current_office] || '', 
        designationSuffix: designationSuffix[user_current_office] || '', 
        numSuffix: numSuffix[user_current_office] || '' 
      },
      sent_to: sendBoObj,
      action:'New Consumable Issue Request Created',
      time_stamp: new Date()
    }],
    opened: false
  }), [
    uniqueReqId, id, getSingleuser, user_current_office, designation, 
    storeSelectedValue, selectedConsum, Bo_id, designationSuffix, 
    numSuffix, remark, sendBoObj
  ]);
  
  // Validate form function - extracted from submit handler
  const validateForm = useCallback(() => {
    if (!storeSelectedValue) {
      alert.showWarning("Please select a store");
      return false;
    }
    
    if (!hasValidQuantity(selectedConsum)) {
      alert.showWarning("Please enter valid quantities for all items");
      return false;
    }
    
    if (!Bo_id) {
      alert.showError("Branch Office information not found");
      return false;
    }
    
    if (!getSingleuser || !getSingleuser[id]) {
      alert.showError("User details not available");
      return false;
    }
    
    return true;
  }, [storeSelectedValue, hasValidQuantity, selectedConsum, Bo_id, getSingleuser, id, alert]);
  
  // Optimize submit handler with useCallback
  const handleFormSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setIsSubmitting(true);      
      await addNewConsumableReq(userReqData).unwrap();
      alert.showSuccess("Request submitted successfully", "Success", () => {
        // Clear form data
        setSelectedConsum([]);
        setRemark('');
        setAddSpecIndex(1);
        setIsSubmitting(false);
        
        // Navigate to inbox or relevant page
        navigate('/management/consumables');
      });
      
    } catch (error) {
      console.error("Failed to submit request", error);
      alert.showError("Failed to submit request. Please try again: " + (error.message || "Unknown error"));
      setIsSubmitting(false);
    }
  }, [validateForm, addNewConsumableReq, userReqData, navigate, alert]);

  // Store selection handler
  const handleStoreChange = useCallback((event, newValue) => {
    setStoreSelectedValue(newValue);
  }, []);

  // Remark change handler
  const handleRemarkChange = useCallback((e) => {
    setRemark(e.target.value);
  }, []);

  return (
    <Container>
      <HeadingWrap>
        <Span>Request for Consumable Issue</Span>
      </HeadingWrap>

      <SingleSelectCont>
        <Autocomplete
          fullWidth
          size="small"
          options={storeOptions}
          value={storeSelectedValue || null}
          onChange={handleStoreChange}
          renderInput={(params) => <TextField {...params} label="Please Select Store" />}
          isOptionEqualToValue={(option, value) => {
            // Handle null and empty values
            if (!value) return true;
            return option === value;
          }}
        />
      </SingleSelectCont>

      {storeSelectedValue?.length ? (
        <SubHeadingField>Please Select Consumable(s) for issue</SubHeadingField>
      ):null}

      {storeSelectedValue?.length ? (
        <form onSubmit={handleFormSubmit} style={{marginTop:"20px", marginLeft:"8px"}}>
          <UnivConsumSelector
            addSpecIndex={addSpecIndex}
            setAddSpecIndex={setAddSpecIndex}
            selectedConsum={selectedConsum}
            setSelectedConsum={setSelectedConsum}
            storeSelectedValue={storeSelectedValue}
            hidePartNumber={true}
          />

          {hasValidQuantity(selectedConsum) && (
            <>
              <RemarkContainer>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Add Remark"
                  variant="outlined"
                  value={remark}
                  onChange={handleRemarkChange}
                />
              </RemarkContainer>
              <Issuebutton 
                type='submit' 
                disabled={isAddingReq || isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request to BO'}
              </Issuebutton>
            </>
          )}
        </form>
      ) : null}
    </Container>
  );
};

export default React.memo(ConsumIssueReq);
